import { baseApi, resourceScopeTag } from '@krp/client-runtime'

import type { CatalogLinkResult, LinkedProduct } from '../model/types'
import { decodeLinkedProducts, decodeLinkResult } from './contracts'
import { PLATFORM_CATALOG_URLS } from './urls'

/**
 * Связи товаров в общем `baseApi` (R04): префикс `platformCatalog`, свой тип тега.
 * Список связанных товаров ресурса несёт `resourceScopeTag(rid)`: смена области
 * ресурса перечитывает его. Команды несут `operationId` намерения человека и не
 * повторяются transport-ом (ADR-11); повтор тем же ключом сервер узнаёт сам.
 */
export const PLATFORM_CATALOG_TAG = 'PlatformCatalogLink' as const

export interface CatalogLinkArgs {
  operationId: string
  reason: string
  productName: string | null
  cards: readonly { resourceId: string; remoteKey: string }[]
}

export interface CatalogUnlinkArgs {
  operationId: string
  productId: string
  resourceId: string
  reason: string
}

export const catalogLinksApi = baseApi
  .enhanceEndpoints({ addTagTypes: [PLATFORM_CATALOG_TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      platformCatalogLinks: build.query<readonly LinkedProduct[], { resourceId: string }>({
        query: ({ resourceId }) => ({ url: PLATFORM_CATALOG_URLS.links, params: { resourceId }, decode: decodeLinkedProducts }),
        providesTags: (_result, _error, { resourceId }) => [
          { type: PLATFORM_CATALOG_TAG, id: resourceId },
          resourceScopeTag(resourceId),
        ],
      }),
      platformCatalogLink: build.mutation<CatalogLinkResult, CatalogLinkArgs>({
        query: ({ operationId, reason, productName, cards }) => ({
          url: PLATFORM_CATALOG_URLS.links,
          method: 'POST',
          body: { operationId, reason, productName, cards },
          decode: decodeLinkResult,
        }),
        // Карточка может присоединиться к товару, связанному и с третьим ресурсом: перечитываются все списки.
        invalidatesTags: [PLATFORM_CATALOG_TAG],
      }),
      platformCatalogUnlink: build.mutation<CatalogLinkResult, CatalogUnlinkArgs>({
        query: ({ operationId, productId, resourceId, reason }) => ({
          url: PLATFORM_CATALOG_URLS.unlink(productId, resourceId),
          method: 'POST',
          body: { operationId, reason },
          decode: decodeLinkResult,
        }),
        // Связи товара видны в списках всех его ресурсов — перечитываются все списки.
        invalidatesTags: [PLATFORM_CATALOG_TAG],
      }),
    }),
  })

export const { platformCatalogLinks, platformCatalogLink, platformCatalogUnlink } = catalogLinksApi.endpoints
